import "./custom.css";

import {
  icon,
  regular,
  solid,
} from "@fortawesome/fontawesome-svg-core/import.macro";
import { styled } from "@mui/system";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import {
  User,
  Workspace,
  WorkspaceMembership,
} from "../../components/graphql/schema.graphql";
import { useBasicData } from "../../hooks";
import { getConnectionNodesTyped, RelayConnection } from "../../utils/relay";
import { truncate } from "../../utils/strings";
import { Box, Button, Icon, Link as UiLink, Typography } from "../ui-v2";
import { ForgeIcon } from "../ui-v2/icons";
import DefaultProfileImage from "./DefaultProfileImage";
import UpgradeToTeamPrompt from "./UpgradeToTeamPrompt";
import ReleaseNotesBox from "./WhatsNewBox";
import WorkspaceSelectionButton from "./WorkspaceSelectionButton";

const SIDEBAR_WIDTH = 230;

type AppMenuWorkspace = Pick<
  Workspace,
  "__typename" | "id" | "name" | "profilePicture" | "myRole" | "myPermissions"
>;

type AppMenuMembership = Pick<WorkspaceMembership, "__typename" | "id"> & {
  workspace: AppMenuWorkspace;
};

type AppMenuUser = Pick<User, "__typename" | "profilePicture"> & {
  personalWorkspace?: AppMenuWorkspace;
  memberships: RelayConnection<AppMenuMembership>;
};

type AppMenuItem = {
  id: string;
  name: string;
  route: string;
  icon: JSX.Element;
  colors: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  disabled: boolean;
  dataCy?: string;
};

function getAppMenuItems(
  workspace: AppMenuWorkspace,
  me: AppMenuUser,
): AppMenuItem[] {
  const items = [];
  if (!workspace || !workspace.myRole) {
    if (me) {
      if (me.personalWorkspace) {
        items.push({
          id: me.personalWorkspace.id,
          name: me.personalWorkspace.name,
          route: `/${me.personalWorkspace.name}`,
          icon: <img src={me.profilePicture} className="w-[20px] rounded" />,
          colors: "text-layer-600 bg-transparent",
          className: "mb-1",
          disabled: false,
          dataCy: "sidebar-workspace",
        });
      }
      getConnectionNodesTyped(me.memberships)
        ?.filter((w) => w.workspace?.id !== me.personalWorkspace?.id)
        ?.sort((a, b) => a.workspace.name.localeCompare(b.workspace.name))
        ?.forEach((membership) => {
          items.push({
            id: membership.workspace.id,
            name: truncate(membership.workspace.name, 18),
            route: `/${membership.workspace.name}`,
            icon: membership.workspace.profilePicture ? (
              <img
                src={membership.workspace.profilePicture}
                className="w-[20px] rounded bg-layer-700 before:content-['🏢'] before:w-full before:h-full before:block before:bg-layer-600 before:grayscale before:text-center"
              />
            ) : (
              <DefaultProfileImage
                workspaceName={membership.workspace.name}
                className="text-xs w-[20px] rounded"
              />
            ),
            colors: "text-layer-600 bg-white/75",
            className: "mb-1",
            disabled: false,
            dataCy: "sidebar-workspace",
          });
        });
    }
    return items;
  } else {
    items.push({
      id: "home",
      name: "Home",
      route: `/${workspace.name}`,
      icon:
        location.pathname === `/${workspace.name}` ? (
          <Icon size="medium" icon={solid("house-window")} />
        ) : (
          <Icon size="medium" icon={regular("house-window")} />
        ),
      colors: "text-[inherit] bg-transparent",
      className: "mb-1",
      disabled: false,
    });
    if (workspace.myPermissions.includes("asset:view")) {
      items.push({
        id: "drive",
        name: "Drive",
        route: `/${workspace.name}/drive`,
        icon: location.pathname.includes(`/${workspace.name}/drive`) ? (
          <Icon icon={solid("layer-group")} />
        ) : (
          <Icon icon={regular("layer-group")} />
        ),
        colors: "text-[inherit] bg-transparent",
        className: "mb-1",
        disabled: false,
      });
    }
    items.push({
      id: "styles",
      name: "Styles",
      route: `/${workspace.name}/styles`,
      icon: location.pathname.includes(`/${workspace.name}/styles`) ? (
        <Icon icon={solid("palette")} />
      ) : (
        <Icon icon={regular("palette")} />
      ),
      colors: "text-[inherit] bg-transparent",
      className: "mb-1",
      disabled: false,
    });

    if (
      workspace.myPermissions.includes("inference:view_history") ||
      workspace.myPermissions.includes("style:view")
    ) {
      items.push({
        id: "prompts",
        name: "History",
        route: `/${workspace.name}/prompts`,
        icon: location.pathname.includes(`/${workspace.name}/prompts`) ? (
          <Icon icon={solid("clock-rotate-left")} />
        ) : (
          <Icon icon={regular("clock-rotate-left")} />
        ),
        colors: "text-[inherit] bg-transparent",
        className: "mb-3",
        disabled: false,
      });
    }
  }
  return items;
}

const getCreateMenuItems = (workspace: AppMenuWorkspace) => {
  if (!workspace?.name) {
    return [];
  }

  const items = [];

  items.push({
    id: "forge",
    name: "Forge",
    route: `/${workspace.name}/session/new`,
    icon: <ForgeIcon weight="solid" />,
  });

  items.push({
    id: "sketch",
    name: "Canvas",
    route: `/${workspace.name}/assets/new`,
    icon: <Icon icon={solid("pencil")} />,
  });

  return items;
};

interface CommonComponentProps {
  fixed: boolean;
}

const Spacer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fixed",
})<CommonComponentProps>(({ fixed }) => ({
  width: fixed ? SIDEBAR_WIDTH : 0,
  flexShrink: 0,
  transitionProperty: "all",
  transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
  transitionDuration: "500ms",
}));

interface SidebarContainerProps extends CommonComponentProps {
  open: boolean;
}

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fixed" && prop !== "open",
})<SidebarContainerProps>(({ theme, fixed, open }) => ({
  position: "fixed",
  width: SIDEBAR_WIDTH,
  height: "100%",
  maxHeight: "100vh",
  transition: "all ease-out 300ms",
  zIndex: 200,
  padding: fixed ? theme.spacing(0, 8, 0, 0) : theme.spacing(16, 4, 16, 4),
  marginLeft: fixed || open ? 0 : -SIDEBAR_WIDTH, // Move sidebar off-screen when not fixed and not open
}));

const FixedPanelBackground = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fixed",
})<CommonComponentProps>(({ theme, fixed }) => ({
  borderRadius: "8px",
  backdropFilter: "blur(12px)",
  position: "absolute",
  left: 0,
  top: 0,
  transition: "all 300ms ease-out",
  transformOrigin: "top left",
  border: `1px solid ${theme.palette.secondary[700]}`,
  backgroundColor: theme.palette.secondary[900],
  ...(fixed
    ? {
        height: 0,
        width: 0,
        opacity: 0,
        transform: "scale(0.95)",
        boxShadow: "none",
      }
    : {
        height: "100%",
        width: "200px",
        opacity: 1,
        transform: "scale(1)",
        boxShadow: "2xl black/40%",
      }),
}));

const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fixed",
})<CommonComponentProps>(({ theme, fixed }) => ({
  transition: "all 300ms ease-out",
  overflowY: "auto",
  color: theme.palette.secondary[300],
  display: "flex",
  overflow: "hidden",
  flexDirection: "column",
  "&::-webkit-scrollbar-track": {
    display: "none",
  },
  ...(fixed
    ? {
        height: "100%",
        width: "100%",
        borderRadius: 0,
        paddingLeft: theme.spacing(4),
        paddingRight: 0,
      }
    : {
        height: "calc(100% - 2px)",
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        width: "200px",
        transform: "translateY(1px)",
      }),
}));

const WorkspaceSelectionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fixed",
})<CommonComponentProps>(({ theme, fixed }) => ({
  userSelect: "none",
  transition: "all 300ms ease-out",
  marginLeft: theme.spacing(-2),
  ...(fixed
    ? {
        marginBottom: theme.spacing(5),
        marginTop: theme.spacing(3),
      }
    : {
        position: "relative",
        zIndex: 1,
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        transform: "translateY(-1px)",
      }),
}));

interface StyledNavLinkProps extends CommonComponentProps {
  isActive: boolean;
  disabled?: boolean;
}

const StyledNavLink = styled(Link, {
  shouldForwardProp: (prop) =>
    prop !== "fixed" && prop !== "disabled" && prop !== "isActive",
})<StyledNavLinkProps>(({ theme, fixed, isActive, disabled = false }) => ({
  height: theme.spacing(7),
  display: "flex",
  flexGrow: 1,
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1),
  borderRadius: "8px",
  color: theme.palette.secondary[200],
  border: "1px solid transparent",
  // Fixed variation styling
  ...(fixed
    ? {
        "&:hover": {
          backgroundColor: theme.palette.secondary[800],
        },
        backgroundColor: isActive
          ? theme.palette.secondary[800]
          : "transparent",
      }
    : {
        "&:hover": {
          backgroundColor: theme.palette.secondary[700],
        },
        backgroundColor: isActive
          ? theme.palette.secondary[700]
          : "transparent",
      }),

  ...(isActive && {
    borderColor: theme.palette.secondary[500],
  }),

  opacity: disabled ? 0.4 : 1,
  pointerEvents: disabled ? "none" : "auto",
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: theme.spacing(7),
  height: theme.spacing(7),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginRight: theme.spacing(0.5),
  padding: 0,
}));

const NavItemsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const CreateHeaderSection = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const StickyFooter = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fixed",
})<CommonComponentProps>(({ theme, fixed }) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  justifyContent: "flex-end",
  transition: "all 300ms ease-out",
  position: "sticky",
  bottom: 0,
  marginBottom: fixed ? theme.spacing(2) : 0,
  backgroundColor: fixed
    ? theme.palette.secondary[950]
    : theme.palette.secondary[900],
}));

type AppNavSidebarProps = {
  fixed: boolean;
  open: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

export default function AppNavSidebar({
  fixed,
  open,
  onMouseEnter,
  onMouseLeave,
}: AppNavSidebarProps) {
  const { workspace: workspaceName } = useParams();
  const { user, workspace, workspaceError } = useBasicData();

  const appMenuItems = useMemo(
    () => getAppMenuItems(workspace, user),
    [workspaceName, user.id, workspace],
  );
  const createMenuItems = useMemo(
    () => getCreateMenuItems(workspace),
    [workspaceName, workspace],
  );

  if (!!workspaceName && !workspace && !workspaceError) {
    return null;
  }

  return (
    <>
      <Spacer fixed={fixed} />
      <SidebarContainer
        fixed={fixed}
        open={open}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-cy="nav-sidebar"
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
          }}
        >
          <FixedPanelBackground fixed={fixed} />
          <ContentContainer fixed={fixed}>
            {workspace?.myRole && (
              <WorkspaceSelectionContainer fixed={fixed}>
                <WorkspaceSelectionButton />
              </WorkspaceSelectionContainer>
            )}

            {workspace?.id === user?.personalWorkspace?.id &&
              user?.memberships.edges.length === 1 && <UpgradeToTeamPrompt />}
            {fixed && <Box></Box>}
            <NavItemsContainer>
              {appMenuItems.map((item) => (
                <StyledNavLink
                  to={item.route}
                  key={`nav-menu-item-${item.id}`}
                  onClick={item.onClick ? item.onClick : () => {}}
                  fixed={fixed}
                  isActive={
                    location.pathname.includes(item.id) ||
                    item.route === location.pathname
                  }
                  disabled={item.disabled}
                >
                  <IconContainer>{item.icon}</IconContainer>
                  <Typography
                    variant="small"
                    fontWeight={700}
                    color="secondary.100"
                  >
                    {item.name}
                  </Typography>
                </StyledNavLink>
              ))}
            </NavItemsContainer>
            <CreateHeaderSection>
              <Typography variant="mini">Create</Typography>
            </CreateHeaderSection>
            <NavItemsContainer>
              {createMenuItems.map((item) => (
                <StyledNavLink
                  to={item.route}
                  key={`nav-menu-item-${item.id}`}
                  fixed={fixed}
                  isActive={location.pathname.includes(item.id)}
                >
                  <IconContainer>{item.icon}</IconContainer>
                  <Typography
                    variant="small"
                    fontWeight={700}
                    color="secondary.100"
                  >
                    {item.name}
                  </Typography>
                </StyledNavLink>
              ))}
            </NavItemsContainer>

            <StickyFooter fixed={fixed}>
              <ReleaseNotesBox />
              <Button
                variant="outlined"
                color="secondary"
                component={UiLink}
                href="https://help.layer.ai/en/"
                target="_blank"
                fullWidth
                sx={{ paddingLeft: 4 }}
                startIcon={
                  <Icon
                    icon={icon({
                      name: "circle-question",
                      family: "sharp",
                      style: "regular",
                    })}
                  />
                }
                endIcon={
                  <Box
                    sx={{
                      fontSize: 12,
                    }}
                  >
                    <Icon icon={regular("circle-question")} />
                  </Box>
                }
              >
                Help Center
              </Button>
            </StickyFooter>
          </ContentContainer>
        </Box>
      </SidebarContainer>
    </>
  );
}
